// src/pages/Moment.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const INSPOS = [
  { emoji: '🚶', title: 'Balade',    desc: 'Marche tranquille',    text: 'Une marche tranquille au parc' },
  { emoji: '☕', title: 'Café',      desc: 'Discuter simplement',  text: 'Un café pour discuter simplement' },
  { emoji: '📚', title: 'Lecture',   desc: 'Lire en silence',      text: 'Lire ensemble dans un lieu calme' },
  { emoji: '💻', title: 'Coworking', desc: 'Travailler ensemble',  text: 'Coworking dans un café' },
  { emoji: '✨', title: 'Autre',     desc: 'Votre idée',           text: '' },
];

export default function Moment() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ titre: '', description: '', lieu: '', date_moment: '', heure_moment: '', lat: null, lng: null });
  const [selectedInspo, setSelectedInspo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const selectInspo = (inspo) => {
    setSelectedInspo(inspo.title);
    if (inspo.text) setForm(f => ({ ...f, titre: inspo.text }));
  };

  // Géolocaliser le moment
  const geolocate = () => {
    navigator.geolocation?.getCurrentPosition(pos => {
      setForm(f => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude }));
    });
  };

  const submit = async e => {
    e.preventDefault();
    if (!form.titre.trim()) { setError('Décrivez votre moment.'); return; }
    setError(''); setLoading(true);
    try {
      await api.post('/moments', form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <main className="k-page" style={{ padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--c-primary-lt)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 36, color: 'var(--c-primary)', fontVariationSettings: "'FILL' 1" }}>check_circle</span>
      </div>
      <h2 style={{ fontFamily: 'Manrope,sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--c-primary)', marginBottom: 10 }}>Moment proposé 🌿</h2>
      <p style={{ fontSize: 15, color: 'var(--c-text-2)', lineHeight: 1.65, marginBottom: 28 }}>
        Les personnes proches pourront le voir et vous contacter simplement.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button className="k-button" onClick={() => navigate('/carte')}>Voir sur la carte</button>
        <button className="k-button-soft" onClick={() => { setSuccess(false); setForm({ titre: '', description: '', lieu: '', date_moment: '', heure_moment: '', lat: null, lng: null }); setSelectedInspo(null); }}>
          Proposer un autre moment
        </button>
      </div>
    </main>
  );

  return (
    <main className="k-page">
      <div style={{ padding: '32px 20px 48px' }}>
        <div style={{ marginBottom: 28 }} className="fade-up">
          <h1 style={{ fontFamily: 'Manrope,sans-serif', fontSize: 28, fontWeight: 800, color: 'var(--c-primary)', letterSpacing: '-0.6px', marginBottom: 8 }}>
            Proposer un moment
          </h1>
          <p style={{ fontSize: 15, color: 'var(--c-text-2)', lineHeight: 1.65 }}>
            Pas besoin d'organiser. Juste une intention simple partagée avec les personnes proches.
          </p>
        </div>

        {/* Inspiration cards */}
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 12, marginBottom: 28, scrollbarWidth: 'thin', scrollbarColor: 'var(--c-border) transparent', paddingRight: 20 }}>
          {INSPOS.map(inspo => (
            <div key={inspo.title}
              onClick={() => selectInspo(inspo)}
              style={{ flexShrink: 0, background: 'var(--c-surface)', border: `1.5px solid ${selectedInspo === inspo.title ? 'var(--c-primary)' : 'var(--c-border)'}`, borderRadius: 'var(--radius-lg)', padding: '14px 16px', cursor: 'pointer', minWidth: 130, background: selectedInspo === inspo.title ? 'var(--c-primary-lt)' : 'var(--c-surface)' }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{inspo.emoji}</div>
              <div style={{ fontFamily: 'Manrope,sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--c-text)', marginBottom: 3 }}>{inspo.title}</div>
              <div style={{ fontSize: 12, color: 'var(--c-text-3)' }}>{inspo.desc}</div>
            </div>
          ))}
        </div>

        <form onSubmit={submit}>
          {error && <div className="k-error">{error}</div>}

          <div style={{ marginBottom: 16 }}>
            <label className="k-label">Votre idée de moment</label>
            <textarea name="titre" className="k-input" placeholder="Décrivez simplement le moment que vous proposez…" value={form.titre} onChange={handle} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="k-label">Lieu <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>(public)</span></label>
            <input name="lieu" className="k-input" placeholder="Ex : Parc, café, bibliothèque…" value={form.lieu} onChange={handle} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label className="k-label">Date <span style={{ fontWeight: 400, textTransform: 'none', fontSize: 11 }}>optionnel</span></label>
              <input name="date_moment" type="date" className="k-input" value={form.date_moment} onChange={handle} />
            </div>
            <div>
              <label className="k-label">Heure <span style={{ fontWeight: 400, textTransform: 'none', fontSize: 11 }}>optionnel</span></label>
              <input name="heure_moment" type="time" className="k-input" value={form.heure_moment} onChange={handle} />
            </div>
          </div>

          {/* Géolocaliser le moment */}
          <button type="button" onClick={geolocate}
            style={{ width: '100%', padding: '10px', marginBottom: 16, background: form.lat ? 'var(--c-primary-lt)' : 'var(--c-surface-2)', color: form.lat ? 'var(--c-primary)' : 'var(--c-text-2)', border: `1px solid ${form.lat ? 'var(--c-primary)' : 'var(--c-border)'}`, borderRadius: 'var(--radius-md)', fontFamily: 'Manrope,sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>my_location</span>
            {form.lat ? 'Position enregistrée ✓' : 'Ajouter ma position actuelle'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--c-accent-lt)', borderRadius: 'var(--radius-md)', padding: '12px 15px', marginBottom: 24 }}>
            <span className="material-symbols-outlined" style={{ color: 'var(--c-accent)', fontSize: 18, flexShrink: 0 }}>shield</span>
            <p style={{ fontFamily: 'Manrope,sans-serif', fontSize: 12, color: 'var(--c-accent)', fontWeight: 600, lineHeight: 1.4 }}>
              Les rencontres se font toujours dans des lieux publics.
            </p>
          </div>

          <button type="submit" className="k-button" disabled={loading}>
            {loading ? <div className="k-spinner" style={{ width: 20, height: 20 }} /> : 'Proposer ce moment'}
          </button>
        </form>
      </div>
    </main>
  );
}
